"use client";

import React, { useEffect, useState } from "react";
import { Search, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="224699" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-20 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.filter(item => item.label !== "Purchase" && item.label !== "TermsOfService").map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group relative py-2 text-[12px] font-default font-semibold uppercase tracking-widest text-foreground/90 transition-colors hover:text-accent-foreground">
                  {item.label}
                  <span className="absolute bottom-0 left-0 h-[1px] w-0 bg-accent-foreground transition-all duration-300 ease-out group-hover:w-full" />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-32 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-6 p-6">
      {menuItems.filter(item => item.label !== "Purchase" && item.label !== "TermsOfService").map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-sm font-default font-semibold uppercase tracking-widest text-foreground transition-colors hover:text-accent-foreground">
            {item.label}
          </Link>)}
    </div>;
}
function SearchWidget() {
  const [searchQuery, setSearchQuery] = useState("");
  return <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="text-foreground/90 transition-colors hover:bg-transparent hover:text-accent-foreground">
          <Search className="size-4" />
          <span className="sr-only">Search Analytics</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3 bg-popover border-border shadow-xl">
        <ProductsSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search timber data..." className="w-full font-mono text-xs" />
      </PopoverContent>
    </Popover>;
}
export default function AutoMotiGalvene() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="gtmx-labs-header" className="w-full bg-background text-foreground">
      <header className={cn("sticky top-0 z-50 w-full transition-all duration-300 ease-in-out", isScrolled ? "bg-background/80 border-b border-border/50 backdrop-blur-md shadow-lg" : "bg-background border-b border-border/15")}>
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex h-20 items-center justify-between">
            
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center text-xl font-bold tracking-tight">
                GTMX Labs
              </Link>
            </div>

            
            <div className="flex-1 flex justify-center">
              <MenuProvider menu_id="25">
                <HeaderMenuConsumer />
              </MenuProvider>
            </div>

            
            <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
              <SearchWidget />
              
              
              <div className="md:hidden">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-foreground/90 transition-colors hover:bg-transparent hover:text-accent-foreground">
                      <Menu className="size-5" />
                      <span className="sr-only">Open Menu</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-64 p-0 mt-2 border-border bg-background/95 backdrop-blur-md shadow-2xl">
                    <MenuProvider menu_id="25">
                      <MobileMenuConsumer />
                    </MenuProvider>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </div>
      </header>
    </section>;
}